/**
 * The floating + button (stage lower-right, both 2D and 3D views): opens the
 * add-element menu — Flooring / Counter / Wall / Object, each added with its
 * defaults — and, while a free element is selected, Duplicate at the top: a
 * complete copy of that element, 4 in right and down. Same OptionMenu visual
 * as the ⊕ add-edge-item popup.
 */

import { css } from '@emotion/react';
import { CUSTOM_AREA_TYPES, CustomAreaType } from '@kaiizen/kai-floor-plan';
import { capitalized, useFloorPlanEditorState, useKeepSelectedElementInSight } from '@kaiizen/kai-floor-plan-editor-core';

import { buttonShadow, ink, lemon } from '../styles';
import { OptionMenu } from './OptionMenu';
import { addFreeElementOfKind, duplicateSelectedElement } from './panel';
import { usePopupOpenState } from './usePopupOpenState';

const DUPLICATE = 'duplicate';
const ADD_OPTIONS: [string, string][] = CUSTOM_AREA_TYPES.map(kind => [kind, capitalized(kind)]);
const ADD_OPTIONS_WITH_DUPLICATE: [string, string][] = [[DUPLICATE, 'Duplicate'], ...ADD_OPTIONS];

const BUTTON_SIZE = 45;

const ADD_ELEMENT_HOST_STYLE = css`
  label: ADD_ELEMENT_HOST_STYLE;
  position: absolute; right: 14px; bottom: 30px; z-index: 42;
  display: flex; flex-direction: column; align-items: flex-end; gap: 8px;
`;

// Lemon = active + significant button (the design-language rule; same as the
// enabled Save button).
const ADD_ELEMENT_BUTTON_STYLE = css`
  label: ADD_ELEMENT_BUTTON_STYLE;
  width: ${BUTTON_SIZE}px; height: ${BUTTON_SIZE}px; border-radius: 50%; padding: 0;
  display: flex; align-items: center; justify-content: center;
  background: ${lemon}; border: 1px solid ${ink}; cursor: pointer;
  box-shadow: ${buttonShadow};
`;

export function AddElementButton(): React.JSX.Element {
  const floorPlanEditorState = useFloorPlanEditorState();
  const keepInSight = useKeepSelectedElementInSight();
  const { open, setOpen, hostRef } = usePopupOpenState();
  const { kind } = floorPlanEditorState.st.selection;
  const freeElementSelected = kind === 'inner' || kind === 'innerCorner';

  const addElement = (option: string): void => {
    setOpen(false);
    keepInSight(() => {
      if (option === DUPLICATE) duplicateSelectedElement(floorPlanEditorState);
      else addFreeElementOfKind(floorPlanEditorState, option as CustomAreaType);
    });
  };

  return (
    <div css={ADD_ELEMENT_HOST_STYLE} ref={hostRef}>
      {open && <OptionMenu options={freeElementSelected ? ADD_OPTIONS_WITH_DUPLICATE : ADD_OPTIONS} onSelect={addElement} testId='add-element-menu' />}
      <button css={ADD_ELEMENT_BUTTON_STYLE} onClick={() => setOpen(!open)} aria-label='Add element' data-testid='add-element-button'>
        <svg width={16} height={16} viewBox='0 0 16 16'>
          <line x1={8} y1={2} x2={8} y2={14} stroke={ink} strokeWidth={2} strokeLinecap='round' />
          <line x1={2} y1={8} x2={14} y2={8} stroke={ink} strokeWidth={2} strokeLinecap='round' />
        </svg>
      </button>
    </div>
  );
}
